import "./asnGRNViewDetails.css";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { GET_CONSIGNMENT_DETAILS_BY_NUMBER, GET_ASN_CORRECTION_LIST } from '../../../common/URLs';
import { PROD } from "../../../common/constants";
import { loaderService } from "../../../common/custom-loader/loader-service";
import { getLocalStorageItem } from "../../../common/localStorageUtils";
import { toast } from 'react-toastify';
import { 
    Fab, Typography, Container, Card, CardContent, Box, 
    Grid, TextField, Divider 
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid';
import { KeyboardArrowLeftRounded } from '@mui/icons-material';
import { handleNumberFieldFocus } from "../../../common/commonFunction";
import apiClient from "../../../axios";

export function AsnCorrectionViewDetails() {
    const navigate = useNavigate();
    const asnData = useLocation().state;
    const [consignmentDetails, setConsignmentDetails] = useState([]);
    const [documentsArray, setDocumentsArray] = useState([]);

    useEffect(() => {
        getViewDetails();
    }, [asnData]);

    const getViewDetails = () => {
        const isPending = asnData?.tabValue === "pending";
        const headers = {
            Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
        };
        const endpoint = isPending ? GET_ASN_CORRECTION_LIST : GET_CONSIGNMENT_DETAILS_BY_NUMBER;
        const data = isPending
            ? {
                asnNumber: asnData?.selectedRow?.asnNo,
                materialType: asnData?.selectedRow?.asnType || "RawBamboo",
                roleType: localStorage.getItem("role") || "",
                isSentBackForAsnCorrection: false,
                isRequestApproved: false,
            }
            : {
                consignmentNumber: asnData?.selectedRow?.consignmentNo,
            };

        loaderService.showLoader();
        apiClient
            .post(endpoint, data, { headers })
            .then(res => {
                setConsignmentDetails(isPending ? res.data?.asnList?.[0] : res.data);
            })
            .catch(function (err) {
                console.log(err);
                toast.error(err.message);
            })
            .finally(() => loaderService.hideLoader());
    };

    const onUploadCallBack = (docArray) => {
        setDocumentsArray(docArray);
    };

    const inventoryRows =
        consignmentDetails?.consignmentSourceDetails?.flatMap((source) =>
            source.consignmentPickupRbCbs?.map((bundle) => ({
                ...bundle.inventoryDetails,
                bundleId: bundle.inventoryDetails?.bundleId ?? bundle.bundleId,
                totalMoisture: consignmentDetails.totalMoisture,
                destinationTotalMoisture: consignmentDetails.destinationTotalMoisture,
            })) ?? []
        ) ?? [];

    const columns: GridColDef[] = [
        { field: 'bundleId', headerName: 'Lot ID', width: 260 },
        {
            field: 'pickupWeight',
            headerName: 'Weight (in MT)',
            width: 130,
            valueGetter: (params) => params.row.pickupWeight?.toFixed(4),
        },
        {
            field: 'arrivalWeight',
            headerName: 'Weight (in MT)',
            width: 130,
            valueGetter: (params) => params.row.arrivalWeight?.toFixed(4),
        },
        {
            field: 'isUnrestrictedInventory',
            headerName: 'Un-Restricted Inventory',
            width: 180,
            valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
            field: 'isRestrictedInventory',
            headerName: 'Restricted Inventory',
            width: 150,
            valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
            field: 'isScrapInventory',
            headerName: 'Scrap Inventory',
            width: 150,
            valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
        {
            groupId: 'Loading',
            children: [{ field: 'pickupMoisture' }, { field: 'pickupWeight' }],
        },
        {
            groupId: 'Unloading',
            children: [
                { field: 'arrivalMoisture' },
                { field: 'arrivalWeight' },
                { field: 'isUnrestrictedInventory' },
                { field: 'isRestrictedInventory' },
                { field: 'isScrapInventory' },
            ],
        },
    ];

    const [commentRows] = useState([
        { action: "Consignment Creation", comment: consignmentDetails.consignmentComments, isValueThere: !!consignmentDetails.consignmentComments },
        { action: "Pick-up", comment: consignmentDetails.pickupComments, isValueThere: !!consignmentDetails.pickupComments },
        { action: "Trip Start", comment: consignmentDetails.tripStartComments, isValueThere: !!consignmentDetails.tripStartComments },
        { action: "Measurement", comment: consignmentDetails.measurementComments, isValueThere: !!consignmentDetails.measurementComments },
        { action: "Unloading", comment: consignmentDetails.unloaderComments, isValueThere: !!consignmentDetails.unloaderComments },
        { action: "Generate GRN", comment: consignmentDetails.destinationComments, isValueThere: !!consignmentDetails.destinationComments }
    ]);

    const commentColumns = [
        { field: "action", headerName: "Action", width: 220 },
        { field: "comment", headerName: "Comment", flex: 1 }
    ];

    const goBack = () => {
        if (asnData?.pageName === "asnCorrectionApproval") {
            navigate('../asnCorrectionApproval', { state: { tabValue: asnData?.tabValue, tabValueMaterial: asnData?.tabValueMaterial, pageName: asnData?.pageName } });
        } else {
            navigate(-1);
        }
    };

    // Clean Minimal styling for DataGrids
    const dataGridStyles = {
        border: 0,
        '& .MuiDataGrid-columnHeaders': { 
            backgroundColor: '#fafafa', 
            borderBottom: '1px solid #f0f0f0' 
        },
        '& .MuiDataGrid-cell': { 
            borderBottom: '1px solid #f0f0f0' 
        },
        '& .MuiDataGrid-columnSeparator': {
            display: 'none',
        }
    };

    return (
        <Container maxWidth="xl">
            {!PROD && (
                <Typography variant="caption" display="block" textAlign="center" color="text.secondary" mb={1}>
                    PIT08 (Non-Prod)
                </Typography>
            )}

            {/* Header Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Fab 
                    onClick={goBack} 
                    size="small" 
                    aria-label="back" 
                    title='Back'
                    elevation={0}
                    sx={{ mr: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
                >
                    <KeyboardArrowLeftRounded />
                </Fab>
                <Typography variant="h5" fontWeight="600" color="text.primary">
                    ASN Correction View Details
                </Typography>
            </Box>

            {/* Main Content Card */}
            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 4 }}>
                    
                    {/* --- Measurement Section --- */}
                    <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                        Measurement Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={4}>
                            <TextField fullWidth size="small"
                                label='Gross weight (in KG) measured'
                                disabled
                                name="destinationGrossWeight"
                                value={Number((asnData?.selectedRow?.destinationGrossWeight) * 1000).toFixed(2) || ''}
                            />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <TextField fullWidth size="small"
                                disabled
                                label='Tare weight (in KG) measured'
                                name="destinationTareWeight"
                                value={Number((asnData?.selectedRow?.destinationTareWeight) * 1000).toFixed(2) || ''}
                            />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <TextField fullWidth size="small"
                                disabled
                                type="number"
                                onFocus={handleNumberFieldFocus}
                                label='Net weight(in KG) as per measurement'
                                name="destinationNetWeight"
                                value={Number((asnData?.selectedRow?.destinationNetWeight) * 1000).toFixed(2) || ''}
                            />
                        </Grid>

                        {asnData?.tabValue === "pending" && (
                            <>
                                <Grid item xs={12} lg={4}>
                                    <TextField fullWidth size="small"
                                        label='Approved Gross Weight (kg)'
                                        disabled
                                        value={Number((consignmentDetails?.asnCorrectionDestinationGrossWeight || 0) * 1000).toFixed(2)}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <TextField fullWidth size="small"
                                        label='Approved Tare Weight (kg)'
                                        disabled
                                        value={Number((consignmentDetails?.asnCorrectionDestinationTareWeight || 0) * 1000).toFixed(2)}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <TextField fullWidth size="small"
                                        label='Approved Net Weight (kg)'
                                        disabled
                                        value={Number((consignmentDetails?.asnCorrectionDestinationNetWeight || 0) * 1000).toFixed(2)}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* --- Inventory Section --- */}
                    <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                        Inventory & Lots
                    </Typography>
                    <Grid container spacing={3}>
                        {asnData?.isDbsAsn ? (
                            <>
                                <Grid item xs={12} lg={4}>
                                    <TextField fullWidth size="small"
                                        label='Lot ID'
                                        value={consignmentDetails?.consignmentSourceDetails?.consignmentPickupRbCbs?.length > 0 ? consignmentDetails?.consignmentSourceDetails?.consignmentPickupRbCbs?.bundleId : ""}
                                        disabled />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <TextField fullWidth size="small"
                                        label='Barcode'
                                        value={consignmentDetails?.consignmentSourceDetails?.consignmentPickupRbCbs?.length > 0 ? consignmentDetails?.consignmentSourceDetails?.consignmentPickupRbCbs?.barcodeNo : ""}
                                        disabled />
                                </Grid>
                            </>
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={{ height: 400, width: '100%', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                    {consignmentDetails?.consignmentSourceDetails && (
                                        <DataGrid
                                            experimentalFeatures={{ columnGrouping: true }}
                                            columnGroupingModel={columnGroupingModel}
                                            rows={inventoryRows}
                                            columns={columns}
                                            pageSizeOptions={[5, 10, 20]}
                                            initialState={{
                                                pagination: { paginationModel: { pageSize: 10 } },
                                            }}
                                            getRowId={(row) => row.bundleId}
                                            disableRowSelectionOnClick
                                            sx={dataGridStyles}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {/* {commentRows?.filter(item => item.isValueThere).length > 0 && ( */}
                        <>
                            <Divider sx={{ my: 2 }} />
                            
                            {/* --- Comments Section --- */}
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                                Action Comments
                            </Typography>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Box sx={{ height: 300, width: '100%', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                        <DataGrid
                                            rows={commentRows?.filter(item => item.isValueThere)}
                                            columns={commentColumns}
                                            pageSizeOptions={[5, 10]}
                                            initialState={{
                                                pagination: { paginationModel: { pageSize: 10 } },
                                            }}
                                            getRowId={(row) => row.action}
                                            disableRowSelectionOnClick
                                            hideFooterSelectedRowCount
                                            sx={dataGridStyles}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    {/* // )} */}
                </CardContent>
            </Card>
        </Container>
    );
}
